// MaterialPreview's header projection, pure: the material's recorded
// provenance → the facts its quiet header states. Total, no I/O, no casts.
//
// - `aiAuthored` mirrors the recorded authorship (never inferred, never
//   over-claimed — a legacy material without the field reads human).
// - `createdOn` is the parsed creation moment, `undefined` when the backend has
//   no timestamp OR it does not parse (never a fake epoch date).
// - `derivedFrom` is the lineage the "Created from" chips open, each named by
//   its friendliest known title: the source's own bibliographic title when
//   enrichment read one, else its stored title minus any redundant kind suffix.

import type { MaterialProvenance } from "../../app/ports/resource.js";
import type { SourceItem } from "../../app/ports/workspace.js";
import { resourceDisplayTitle } from "../Navigation/displayTitle.js";
import type { ResourceRef } from "../Navigation/types.js";

export type MaterialPreviewHeader = {
    readonly title: string;
    readonly aiAuthored: boolean;
    readonly createdOn: Date | undefined;
    readonly derivedFrom: readonly ResourceRef[];
};

// Resource id → the friendly title a lineage chip shows instead of the stored
// one (a filename, typically).
export type FriendlyTitles = ReadonlyMap<string, string>;

// The bibliographic titles the library's sources carry (absent ⇒ no entry).
export const friendlySourceTitles = (
    sources: readonly SourceItem[],
): FriendlyTitles =>
    new Map(
        sources.flatMap((s): [string, string][] =>
            s.bibliography !== undefined ? [[s.id, s.bibliography.title]] : [],
        ),
    );

const parseDate = (iso: string | undefined): Date | undefined => {
    if (iso === undefined) return undefined;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? undefined : d;
};

export const materialPreviewHeader = (
    title: string,
    provenance: MaterialProvenance,
    friendly: FriendlyTitles = new Map(),
): MaterialPreviewHeader => ({
    title,
    aiAuthored: provenance.authoredByAgent,
    createdOn: parseDate(provenance.createdAt),
    derivedFrom: provenance.derivedFrom.map((ref) => ({
        ...ref,
        title:
            friendly.get(ref.id) ??
            resourceDisplayTitle(ref.title, ref.type, ref.title),
    })),
});
